import DasbboardAPI from '@/api/dashboard.api.js'
import SellerAccountAPI from '@/api/seller-account.api'
import BuyerAccountAPI from '@/api/buyer-account.api'
import moment from 'moment-timezone'

const state = {
  buyerDashboardGraphData: [],
  buyerDashboardInsights: {},
  buyerDashboardCampaigns: [],
  buyerAutoRefresh: false,
  buyerCampaignChannsels: [],
  sellerDashboardGraphData: [],
  sellerDashboardInsights: {},
  sellerDashboardOffers: [],
  sellerAutoRefresh: false,
  sellerAvailableOffers: [],
  sellerPayouts: [],
  chargeReceipts: [],
  creditCardChargesFetched: false,
  advancePayRequests: [],
  systemAccountBankDetails: {},
}

const getters = {
}

const actions = {
  async getBuyerDashboardData({ commit }, { from, to, timezone }) {
    const response = await DasbboardAPI.getBuyerDashboardData({
      from,
      to,
      timezone
    })
    commit('SET_BUYER_DASHBOARD_GRAPH_DATA', response.data.graphData)
    commit('SET_BUYER_DASHBOARD_INSIGHTS', response.data.insights)
    return response.data
  },
  async getSellerDashboardData({ commit, rootGetters }, { from, to }) {
    const accountId = rootGetters['auth/accountId']
    
    if (!accountId) return

    try {
      const mFrom = moment(from)
      const mTo = moment(to)
      const diffDays = mTo.diff(mFrom, 'days')
      
      let granularityColumn = 'datesByYearMonthDay'
      if (diffDays >= 180) {
        granularityColumn = 'datesByYearMonth'
      } else if (diffDays > 32) {
        granularityColumn = 'datesByYearMonthWeek'
      }

      // 1. Fetch data in parallel
      const [rpcResponse, offersResponse] = await Promise.all([
        DasbboardAPI.getRpcByCategory(accountId, {
          startDate: from.toISOString(),
          endDate: to.toISOString(),
          granularityColumn
        }),
        rootGetters['auth/hasPermission']('campaigns')
          ? DasbboardAPI.getSellerOffers(accountId) : Promise.resolve({ data: [] })
      ])
      
      // 2. Process RPC Records
      const data = rpcResponse.data
      let records = []
      if (data && data.report && data.report.records) {
        records = data.report.records
      } else if (data && Array.isArray(data)) {
        records = data
      } else if (data && data.records) {
        records = data.records
      }
      
      // 3. Process Offers (Fetch Applied Offers as well)
      // Unwrap offers if wrapped in sellerOffer, preserving Wrapper ID for matching
      const rawOffers = offersResponse.data || []
      const offers = rawOffers.map(o => (o.sellerOffer ? { ...o.sellerOffer, _wrapperId: o.id } : o))
      
      let appliedOffers = []
      try {
        const appliedResponse = rootGetters['auth/hasPermission']('campaigns')
          ? await DasbboardAPI.getSellerAppliedOffers(accountId) : { data: [] }
        const rawApplied = appliedResponse.data || []
        appliedOffers = rawApplied.map(o => (o.sellerOffer ? { ...o.sellerOffer, _wrapperId: o.id } : o))
      } catch (e) {
        console.warn("Failed to fetch applied offers", e)
      }
      
      const appliedOffersSet = new Set()
      appliedOffers.forEach(o => {
        if (o.id) appliedOffersSet.add(o.id)
        if (o.name) appliedOffersSet.add(o.name.trim().toLowerCase())
      })
      
      const offerMap = new Map()
      const offerKeysMap = new Map() // Maps various keys to SET of Offer Names

      offers.forEach(offer => {
        if (offer.name) {
          offerMap.set(offer.name, offer)
          
          const addKey = (key, val) => {
            const k = String(key).trim().toLowerCase()
            if (!offerKeysMap.has(k)) {
              offerKeysMap.set(k, new Set())
            }
            offerKeysMap.get(k).add(val)
          }

          addKey(offer.name, offer.name)
          
          // Generate alternate keys
          const categoryName = offer.category?.name || offer.category || offer.name || ''
          let country = 'US'
          if (offer.language && offer.language.id) {
            const lang = offer.language.id.toLowerCase()
            if (lang === 'en') country = 'US'
            else if (lang === 'es') country = 'ES'
            else country = lang.toUpperCase()
          } else if (offer.country) {
            country = offer.country
          }
          
          const formattedKey = `${categoryName} (${country})`
          
          addKey(formattedKey, offer.name)
          addKey(categoryName, offer.name)
          
          if (offer.id) addKey(offer.id, offer.name)
          if (offer._wrapperId) addKey(offer._wrapperId, offer.name)
          
          // Index Campaign ID if present (common link)
          if (offer.campaignId) addKey(offer.campaignId, offer.name)
          if (offer.campaign && offer.campaign.id) addKey(offer.campaign.id, offer.name)
        }
      })
      
      // 4. Aggregate Stats and Enrich Records for Graph
      const performanceMap = {} // Keyed by Offer Name
      let totalRevenue = 0
      let totalCalls = 0
      let totalBillable = 0
      
      // Set to track which offers actually have data
      const offersWithData = new Set()

      records.forEach(r => {
        // Enforce Campaign Name / Category Name mapping
        const campaignName = r.campaignName || r.categoryName || ''
        const distinctKey = campaignName.trim().toLowerCase()
        
        // Multi-key lookup (handle One-to-Many)
        let matchedOfferNames = []
        if (offerKeysMap.has(distinctKey)) {
          matchedOfferNames = Array.from(offerKeysMap.get(distinctKey))
        } else {
          const idKey = r.campaignId ? r.campaignId.toString().trim().toLowerCase() : (r.campaign && r.campaign.id ? r.campaign.id.toString().trim().toLowerCase() : '')
          if (idKey && offerKeysMap.has(idKey)) {
            matchedOfferNames = Array.from(offerKeysMap.get(idKey))
          }
        }
        
        // Fuzzy/Token Match Fallback
        if (matchedOfferNames.length === 0) {
          let bestMatch = null
          let maxScore = 0
           
          const distinctTokens = distinctKey.toLowerCase().split(/\s+/).filter(t => t.length > 1)

          for (const offer of offers) {
            const offerName = offer.name ? offer.name.trim().toLowerCase() : ''
            if (!offerName || offerName.length < 2) continue  // Skip very short names
              
            const offerTokens = offerName.split(/\s+/).filter(t => t.length > 1)
              
            // Count intersecting tokens
            let matches = 0
            distinctTokens.forEach(dt => {
              if (offerTokens.includes(dt)) matches++
            })
              
            // Calculate score (simple intersection count, or biased by length)
            if (matches > 0) {
              const score = matches
              if (score > maxScore) {
                maxScore = score
                bestMatch = offer.name
              }
            }
              
            // Keep containment fallback as well
            if (distinctKey.toLowerCase().includes(offerName) || offerName.includes(distinctKey.toLowerCase())) {
              // Give a high score for containment
              const score = 10 + offerName.length
              if (score > maxScore) {
                maxScore = score
                bestMatch = offer.name
              }
            }
          }
          if (bestMatch) {
            matchedOfferNames = [bestMatch]
          }
        }
        
        // Use first matched offer for Category Name enrichment
        const firstOfferName = matchedOfferNames[0]
        const firstOffer = firstOfferName ? offerMap.get(firstOfferName) : null
        
        if (firstOffer && (firstOffer.category?.name || firstOffer.category?.id)) {
          r.categoryName = firstOffer.category.name || firstOffer.category.id
        } else {
          r.categoryName = firstOffer ? firstOffer.name : (campaignName || 'Unknown')
        }

        // Standardize metrics based on API response variations
        // User JSON has: callCount, convertedCalls, earningsPerCallGross
        const calls = Number(r.callCount !== undefined ? r.callCount : (r.totalCalls || 0))
        const billable = Number(r.convertedCalls !== undefined ? r.convertedCalls : (r.billableCalls || 0))
        
        // Calculate Total Revenue based on available fields (Amount vs Rate)
        let revenueAmount = 0
        if (r.totalRevenue !== undefined) {
          revenueAmount = r.totalRevenue
        } else if (r.totalAmount !== undefined) {
          revenueAmount = r.totalAmount
        } else if (r.earningsPerCallGross !== undefined) {
          revenueAmount = r.earningsPerCallGross * calls
        } else if (r.earningsPerBillableCallGross !== undefined) {
          revenueAmount = r.earningsPerBillableCallGross * billable
        }

        const revenue = revenueAmount

        // Normalize record for UI consumption
        r.totalCalls = calls
        r.billableCalls = billable
        r.totalRevenue = revenue
        r.earningsPerBillableCallGross = revenue // Storing total here for compatibility if needed, though name implies rate

        // Update Totals
        totalRevenue += revenue
        totalCalls += calls
        totalBillable += billable

        // Update Performance Map
        if (matchedOfferNames.length > 0) {
          matchedOfferNames.forEach(name => {
            offersWithData.add(name)
            if (!performanceMap[name]) {
              performanceMap[name] = { calls: 0, billable: 0, revenue: 0 }
            }
            performanceMap[name].calls += calls
            performanceMap[name].billable += billable
            performanceMap[name].revenue += revenue
          })
        } else {
          // Fallback
          if (campaignName) {
            if (!performanceMap[campaignName]) {
              performanceMap[campaignName] = { calls: 0, billable: 0, revenue: 0 }
            }
            performanceMap[campaignName].calls += calls
            performanceMap[campaignName].billable += billable
            performanceMap[campaignName].revenue += revenue
          }
        }
      })

      // 5. Calculate Insights
      const conversionPercentage = totalCalls > 0 ? (totalBillable / totalCalls) * 100 : 0
      const rpc = totalCalls > 0 ? (totalRevenue / totalCalls) : 0
      const billableRpc = totalBillable > 0 ? (totalRevenue / totalBillable) : 0
      
      const insights = {
        revenue: totalRevenue,
        calls: totalCalls,
        billable: totalBillable,
        conversionPercentage,
        rpc: isNaN(rpc) ? 0 : rpc,
        billableRpc: isNaN(billableRpc) ? 0 : billableRpc,
        qualityScore: 0
      }
      
      commit('SET_SELLER_DASHBOARD_INSIGHTS', insights)

      // 6. Merge Stats into Offers for Table
      
      const mergedOffers = offers.map(offer => {
        const isApplied = appliedOffersSet.has(offer.id) || (offer.name && appliedOffersSet.has(offer.name.trim().toLowerCase()))
        
        const stats = performanceMap[offer.name] || { calls: 0, billable: 0, revenue: 0 }
        
        const convPct = stats.calls > 0 ? (stats.billable / stats.calls) * 100 : 0
        const offerRpc = stats.calls > 0 ? (stats.revenue / stats.calls) : 0
        
        return {
          ...offer,
          callCount: stats.calls,
          convertedCalls: stats.billable,
          totalRevenue: stats.revenue,
          conversionPercentage: convPct,
          rpc: isNaN(offerRpc) ? 0 : offerRpc,
          winRatio: offer.winRatio || 0,
          avgBid: offer.avgBid || 0,
          // Ensure we have a string for category
          categoryName: offer.category?.name || offer.category || '',
          isApplied: isApplied
        }
      })
      
      // Sort: Applied First
      mergedOffers.sort((a, b) => {
        if (a.isApplied && !b.isApplied) return -1
        if (!a.isApplied && b.isApplied) return 1
        return 0
      })
      
      commit('SET_SELLER_DASHBOARD_GRAPH_DATA', records)
      commit('SET_SELLER_OFFERS', mergedOffers) 

    } catch (e) {
      console.error('Failed to fetch seller dashboard data', e)
      commit('SET_SELLER_DASHBOARD_GRAPH_DATA', [])
      commit('SET_SELLER_OFFERS', [])
      commit('SET_SELLER_DASHBOARD_INSIGHTS', {})
    }
  },
  async getBuyerCampaigns({ commit }) {
    const response = await DasbboardAPI.getBuyerCampaigns()
    commit('SET_BUYER_CAMPAIGNS', response.data)
    return response.data
  },
  async getSellerOffers({ rootGetters, commit }) {
    const accountId = rootGetters['auth/accountId']
    const response = await DasbboardAPI.getSellerOffers(accountId)
    commit('SET_SELLER_OFFERS', response.data)
    return response.data
  },
  async getBuyerCampaignChannels({ commit }, { campaignId }) {
    const response = await DasbboardAPI.getBuyerCampaignChannels({ campaignId })
    commit('SET_BUYER_CAMPAIGN_CHANNELS', response.data)
    return response.data
  },
  async getSellerAvailableOffers({ commit }) {
    const response = await DasbboardAPI.getSellerAvailableOffers()
    commit('SET_SELLER_AVAILABLE_OFFERS', response.data)
    return response.data
  },
  async getSellerPayouts({ rootGetters, commit }) {
    let accountId = rootGetters['auth/accountId']

    const response = await SellerAccountAPI.getSellerPayouts(accountId)
    commit('SET_SELLER_PAYOUTS', response.data)
    return response.data
  },

  async getCreditCardCharges({ rootGetters, commit }) {
    // Reset the fetched flag before making the API call to handle account switching
    commit('SET_CREDIT_CARD_CHARGES_FETCHED', false)

    let accountId = rootGetters['auth/accountId']

    const response = await BuyerAccountAPI.getCreditCardCharges(accountId)
    commit('SET_CREDIT_CARD_CHARGES', response.data)
    commit('SET_CREDIT_CARD_CHARGES_FETCHED', true)
    return response.data
  },

  async getSystemAccountBankDetails({commit }) {
    const response = await BuyerAccountAPI.getSystemAccountBankDetails()
    commit('SET_BANK_DETAILS', response.data)
    return response.data
  },

  async getAdvancePayRequests({ rootGetters, commit }) {
    let accountId = rootGetters['auth/accountId']

    const response = await BuyerAccountAPI.getAdvancePayRequests(accountId)
    commit('SET_ADVANCE_PAY_REQUESTS', response.data)
    return response.data
  },

  async createAdvancePaymentRequest({ rootGetters }, advancePayRequest) {
    let accountId = rootGetters['auth/accountId']

    const response = await BuyerAccountAPI.createAdvancePaymentRequest({
      accountId: accountId,
      advancePayRequest: advancePayRequest
    })

    return response.data
  },

  async generateInvoiceDownloadLink({ rootGetters }, { invoiceId }) {
    let accountId = rootGetters['auth/accountId']

    const response = await DasbboardAPI.generateInvoiceDownloadLink({accountId, invoiceId })
    return response.data
  }
}

const mutations = {
  SET_BUYER_DASHBOARD_GRAPH_DATA(state, graphData) {
    state.buyerDashboardGraphData = graphData
  },
  SET_BUYER_DASHBOARD_INSIGHTS(state, insights) {
    state.buyerDashboardInsights = insights
  },
  SET_BUYER_CAMPAIGNS(state, campaigns) {
    state.buyerDashboardCampaigns = campaigns
  },
  TOGGLE_BUYER_AUTO_REFRESH(state) {
    state.buyerAutoRefresh = !state.buyerAutoRefresh
  },
  TOGGLE_BUYER_CALL_LOGS_AUTO_REFRESH(state) {
    state.buyerCallLogsAutoRefresh = !state.buyerCallLogsAutoRefresh
  },
  SET_BUYER_CAMPAIGN_CHANNELS(state, campaignChannels) {
    state.buyerCampaignChannels = campaignChannels
  },
  SET_SELLER_DASHBOARD_GRAPH_DATA(state, graphData) {
    state.sellerDashboardGraphData = graphData
  },
  SET_SELLER_DASHBOARD_INSIGHTS(state, insights) {
    state.sellerDashboardInsights = insights
  },
  SET_SELLER_OFFERS(state, offers) {
    state.sellerDashboardOffers = offers
  },
  TOGGLE_SELLER_AUTO_REFRESH(state) {
    state.sellerAutoRefresh = !state.sellerAutoRefresh
  },
  SET_SELLER_AVAILABLE_OFFERS(state, availableOffers) {
    state.sellerAvailableOffers = availableOffers
  },
  SET_SELLER_PAYOUTS(state, payouts) {
    state.sellerPayouts = payouts
  },
  SET_CREDIT_CARD_CHARGES(state, chargeReceipts) {
    state.chargeReceipts = chargeReceipts
  },
  SET_CREDIT_CARD_CHARGES_FETCHED(state, fetched) {
    state.creditCardChargesFetched = fetched
  },
  SET_ADVANCE_PAY_REQUESTS(state, advancePayRequests) {
    state.advancePayRequests = advancePayRequests
  },
  SET_BANK_DETAILS(state, bankDetails) {
    state.systemAccountBankDetails = bankDetails
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
